import { IcoDragAndDrop16, IcoSparkles16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import useMobileLayout from '@/hooks/use-mobile-layout';
import { type ChatWidgetPosition, POSITION_ANCHOR } from '../../hooks/use-chat-widget-position';
import useDraggablePosition from '../../hooks/use-draggable-position';
type OpenChatButtonProps = {
  onClick: () => void;
  title?: string;
  shortcut?: string;
  className?: string;
  ref?: React.Ref<HTMLButtonElement>;
  // When set, the button can be dragged to reposition it; on release it snaps to
  // the nearest zone and reports it via onPositionChange. Defaults off, so static
  // consumers (e.g. the agent-builder button) are unaffected.
  draggable?: boolean;
  // The corner the button anchors to when not dragging.
  position?: ChatWidgetPosition;
  onPositionChange?: (position: ChatWidgetPosition) => void;
  rightInset?: number;
};
const defaultShortcut = () => {
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.userAgent);
  return `${isMac ? '⌘' : 'Ctrl'} + Shift + P`;
};
const OpenChatButton = ({
  onClick,
  title = 'Chat with Percy',
  shortcut,
  className,
  ref,
  draggable = false,
  position = 'bottom-right',
  onPositionChange,
  rightInset = 0
}: OpenChatButtonProps) => {
  const isMobile = useMobileLayout();
  const {
    t
  } = useTranslation('common', {
    keyPrefix: 'components.privateLayout.nav.mobileNavigation'
  });
  const {
    isDragging,
    dragStyle,
    handlers
  } = useDraggablePosition({
    enabled: draggable,
    onPositionChange
  });
  const anchorsRight = position === 'bottom-right' || position === 'top-right';
  // Inset the horizontal edge only (--percy-inset-x); --percy-gap stays the base gap so the vertical
  // anchor is untouched and the button slides along its edge instead of lifting off the corner.
  const insetX = anchorsRight && rightInset > 0 ? `${rightInset}px` : '0px';
  if (isMobile) return <button type="button" ref={ref} onClick={onClick} className={cx('fixed z-20 right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] [body:has([data-node-panel])_&]:hidden flex h-10 items-center gap-1.5 rounded-full bg-neutral-inverted px-3.5 text-label-3 text-quinary shadow-lg focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary', 'transition-[scale] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-pressed-sm motion-reduce:active:scale-100', className)}>
        <IcoSparkles16 />
        {t($ => $.askPercy)}
      </button>;
  return <button ref={ref} type="button" onClick={onClick} {...handlers}
  // dragStyle overrides these anchor vars while dragging.
  style={{
    ['--percy-gap' as string]: '1.5rem',
    ['--percy-inset-x' as string]: insetX,
    ...dragStyle
  }} className={cx('group fixed z-20 flex items-center gap-3 px-3 py-2.5 bg-elevated border border-tertiary rounded-xl shadow-sm hover:shadow-md hover:bg-hover',
  // No transition while dragging — track the cursor 1:1.
  isDragging ? 'transition-none' : 'transition-all', POSITION_ANCHOR[position], draggable && 'touch-none select-none',
  // Click cursor by default (grab lives on the handle); grabbing while dragging.
  isDragging ? 'cursor-grabbing' : 'cursor-pointer', className)}>
      {/* Drag handle, revealed on hover/drag. Hidden: zero width + -mr-3 cancels the
          parent gap-3 so there's no stray padding before the sparkles icon. */}
      {draggable && <span aria-hidden className={cx(
    // Grab cursor lives here; a drag can still start anywhere on the button.
    'flex items-center justify-center shrink-0 overflow-hidden text-tertiary transition-[width,margin,opacity] duration-150', isDragging ? 'cursor-grabbing' : 'cursor-grab', isDragging ? 'w-4 mr-0 opacity-100' : 'w-0 -mr-3 opacity-0 group-hover:w-4 group-hover:mr-0 group-hover:opacity-100')}>
          <IcoDragAndDrop16 color="tertiary" />
        </span>}
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-neutral-inverted">
        <IcoSparkles16 color="quinary" />
      </div>
      {/* nowrap: keep the pill's shape when dragged against a viewport edge. */}
      <div className="flex flex-col items-start whitespace-nowrap">
        <span className="text-label-3 text-primary">{title}</span>
        <span className="text-caption-2 text-tertiary">{shortcut ?? defaultShortcut()}</span>
      </div>
    </button>;
};
export default OpenChatButton;